import { getStockByID, stockCalculation } from "./stock";
import { uuid } from "../../oscar-pos-core/constants";
import { analytics, triggerLogEvent } from "../../config/firebase";
import { unitConversion } from "../../constants";

import { Q } from "@nozbe/watermelondb";
import { database } from "../index";
import {
  BUSINESS_SCHEMA,
  INVOICE_SCHEMA,
  ORDERS_SCHEMA,
  PAYMENT_SCHEMA,
} from "../schema";

const ordersModal = database.collections.get(ORDERS_SCHEMA);

export const getOrdersByTransaction = async (id) =>
  await ordersModal.query(Q.on(INVOICE_SCHEMA, "id", id)).fetch();

export const getOrdersByPayments = async (id) =>
  await ordersModal.query(Q.on(PAYMENT_SCHEMA, "id", id)).fetch();

export const getOrdersByBusiness = async (id) =>
  await ordersModal.query(Q.on(BUSINESS_SCHEMA, "id", id)).fetch();

export const observeOrdersByInvoiceID = (id) =>
  ordersModal.query(Q.on(INVOICE_SCHEMA, "id", id)).observe();

export const getOrders = async (id) =>
  await ordersModal.query(Q.where("invoice_id", id)).fetchCount();

export const getOrdersByBusinessId = (id) =>
  ordersModal.query(Q.where("business_id", id)).observe();

export const getOrdersCount = async () =>
  await ordersModal.query().fetchCount();

export const getSalesOrders = (orders) => {
  let sales = 0;
  let profit = 0;
  let obj = {};

  try {
    orders.map((val) => {
      if (val.type !== "expense") {
        profit += (val.selling_price - val.cost_price) * val.quantity;
        sales += val.selling_price * val.quantity;

        if (obj[val?.stock?.id]) {
          obj[val?.stock?.id].quantity += val.quantity;
          obj[val?.stock?.id].profit +=
            (val.selling_price - val.cost_price) * val.quantity;
        } else {
          obj[val?.stock?.id] = {
            name: val.name,
            quantity: val.quantity,
            profit: (val.selling_price - val.cost_price) * val.quantity,
          };
        }
      }
    });
    return [Object.values(obj), sales, profit];
  } catch (e) {
  }
};

const prepareInsertionV3 = async (business, invoice, items, type, origin) => {
  const ordersByItemId = {};

  const allRecords = await Promise.all(
    items.map(async (item) => {
      try {
        const [stock, is_secondary] = await stockCalculation(item);

        const order = ordersModal.prepareCreate((field) => {
          field._raw.id = uuid();
          origin == "payment"
            ? field.payment.set(invoice)
            : origin == "pos_txns"
              ? field.txns.set(invoice)
              : field.invoice.set(invoice);
          !item?.is_open_item && field.stock.set(stock);
          field.business.set(business);
          field.name = item.name;
          field.quantity = item?.quantity ?? item?.qty ?? 0;
          field.unit = stock.unit;
          field.cost_price = item?.hasOwnProperty("updated_cost_price")
            ? item.updated_cost_price
            : stock.cost_price;
          field.selling_price = item?.hasOwnProperty("updated_selling_price")
            ? item.updated_selling_price
            : item?.selling_price || stock.selling_price;
          field.tax = stock?.tax || null;
          field.inclusive = stock?.inclusive || true;
          field.type = type == "in" ? "sales" : "expense";
          field.transacted_at = new Date().getTime();
          field.discount = item.discount || 0;
          field.discount_method = item.discount_method || "rs";
          field.is_open_item = item?.is_open_item || false;
          field.tran_type = origin == 'cash' ? 'cash' : origin == 'invoice' ? 'invoice' : origin == 'pos' ? 'pos' : origin == "quick_sale" ? "quick_sale" : origin == "quick_expense" ? "quick_expense" : origin == 'payment' ? 'account' : null;
          field.secondary_unit = stock?.secondary_unit || null;
          field.is_secondary_unit = is_secondary;
          field.unit_conversion = stock?.unit_conversion || null;
        });
        if (item?.id != null) ordersByItemId[item.id] = order;
        return order;
      } catch (e) {
        console.log('ERROR: CREATING-ORDER', e)
      }
    }));
  return { allRecords, ordersByItemId };
};

export const bulkCreateOrdersV3 = async (
  business,
  invoice,
  items,
  type,
  origin,
) => {
  let ordersByItemId;

  await database.write(async () => {
    let count = await getOrdersCount();
    count == 4 && triggerLogEvent(analytics, "user_activated_stock", {});
    const result = await prepareInsertionV3(
      business,
      invoice,
      items,
      type,
      origin,
    );
    ordersByItemId = result.ordersByItemId;
    await database.batch(...result.allRecords);
  });
  return ordersByItemId;
};

export const bulkRestoreStock = async (items, type, v2 = false) => {
  await database.write(async () => {
    const allRecords = await prepareStockRestoreV2(items, type);
    await database.batch(...allRecords);
  });
};

const prepareStockRestoreV2 = async (items, type) => {
  return await Promise.all(
    items.map(async (item) => {
      try {
        const { original, updated } = item;

        let stock = await getStockByID(updated.id);
        let prevUnit = original?.is_secondary_unit
          ? original.secondary_unit
          : original.unit;

        let qty = unitConversion(stock, prevUnit, original.quantity);

        return type == "in"
          ? stock.prepareUpdate((x) => (x.stock += qty))
          : stock.prepareUpdate((x) => (x.stock -= qty));
      } catch (err) {
      }
    }),
  );
};

export const bulkUpdateTransactionStock = async (items, type) => {
  await database.write(async () => {
    const allRecords = await prepareStockUpdationV2(items, type);
    await database.batch(...allRecords);
  });
};

const prepareStockUpdationV2 = async (items, type) => {
  return await Promise.all(
    items.map(async (item) => {
      try {
        const { original, updated } = item;

        let stock = await getStockByID(updated.id);

        let prevUnit = original?.is_secondary_unit
          ? original.secondary_unit
          : original.unit;
        let prevQty = unitConversion(stock, prevUnit, original.quantity);

        let updatedQty =
          type == "in" ? stock.stock + prevQty : stock.stock - prevQty;

        let newUnit = updated?.updated_unit || stock.unit;
        let newQty = unitConversion(stock, newUnit, updated.quantity);

        let finalStock =
          type == "in" ? updatedQty - newQty : updatedQty + newQty;

        if (!Number.isFinite(finalStock)) {
          return;
        }

        return stock.prepareUpdate((x) => (x.stock = finalStock));
      } catch (err) {
      }
    }),
  );
};

export const bulkModifyUpdateStock = async (items, oldItems, type) => {
  await database.write(async () => {
    const allRecords = await prepareStockUpdation(items, oldItems, type);
    await database.batch(...allRecords);
  });
};

const prepareStockUpdation = async (items, oldItems, type) => {
  return await Promise.all(
    oldItems.map(async (oldItem) => {
      try {
        let item = items.find((x) => x.id == oldItem.stock.id);

        if (item) {
          let stock = await getStockByID(item.id);

          return type == "in"
            ? stock.prepareUpdate((i) => {
              i.stock += oldItem.quantity - item.quantity;
            })
            : stock.prepareUpdate((i) => {
              i.stock += item.quantity - oldItem.quantity;
            });
        } else {
          let stock = await getStockByID(oldItem.stock.id);

          return type == "in"
            ? stock.prepareUpdate((i) => {
              i.stock += oldItem.quantity;
            })
            : stock.prepareUpdate((i) => {
              i.stock -= oldItem.quantity;
            });
        }
      } catch (err) {
      }
    }),
  );
};

export const bulkDeleteOrders = async (orders) => {
  await database.write(async () => {
    const allRecords = prepareDeletion(orders);
    await database.batch(...allRecords);
  });
};

const prepareDeletion = (orders) => {
  return orders.map((order) => {
    try {
      return order.original.prepareMarkAsDeleted();
    } catch (err) {
    }
  });
};

export const bulkModifyOrders = async (items, oldItems) => {
  await database.write(async () => {
    const allRecords = prepareUpdation(items, oldItems);
    await database.batch(...allRecords);
  });
};

const prepareUpdation = (items, oldItems) => {
  return oldItems.map((oldItem) => {
    try {
      let item = items.find((x) => x.id == oldItem.stock.id);

      if (item) {
        return oldItem.prepareUpdate((i) => {
          i.name = item.name;
          i.quantity = item.quantity;
          i.cost_price = item.cost_price;
          i.selling_price = item.selling_price;
          i.tax = item.tax;
          i.inclusive = item.inclusive;
          i.discount = item.discount || 0;
          i.discount_method = item.discount_method || "rs";
        });
      } else {
        return oldItem.prepareMarkAsDeleted();
      }
    } catch (err) {
    }
  });
};

export const bulkDeleteOrdersByInvoices = async (allTransactions, type) => {
  if (allTransactions?.length) {
    await database.write(async () => {
      const orders = await getOrdersFromTransactions(allTransactions, type);
      const allRecords = await prepareDeleteInvOrders(orders);
      await database.batch(...allRecords);
    });
  }
};

const getOrdersFromTransactions = async (transactions, type) => {
  let orders = [];

  await Promise.all(
    transactions.map(async (tr) => {
      const order =
        type == "invoice"
          ? await getOrdersByTransaction(tr.id)
          : await getOrdersByPayments(tr.id);
      orders.push(...order);
    }),
  );

  return orders;
};

const prepareDeleteInvOrders = async (orders) => {
  return await Promise.all(
    orders.map((order) => {
      try {
        return order.prepareMarkAsDeleted();
      } catch (err) {
      }
    }),
  );
};

export const bulkUpdateOrders = async (items) => {
  await database.write(async () => {
    const allRecords = await prepareOrderUpdationV2(items)
    await database.batch(...allRecords);
  });
};



const prepareOrderUpdationV2 = async (items) => {
  return await Promise.all(
    items.map(async (item) => {
      try {
        const { original, updated } = item;

        const [stock, is_secondary] = await stockCalculation(updated);

        return original.prepareUpdate((x) => {
          x.selling_price = updated?.hasOwnProperty("updated_selling_price")
            ? updated.updated_selling_price
            : stock.selling_price;
          x.cost_price = updated?.hasOwnProperty("updated_cost_price")
            ? updated.updated_cost_price
            : stock.cost_price;
          x.discount_method = updated?.discount_method || "rs";
          x.secondary_unit = stock?.secondary_unit || null;
          x.quantity = updated.quantity ?? updated?.qty ?? 0;
          x.discount = updated?.discount || 0;
          x.is_secondary_unit = is_secondary;
          x.inclusive = stock?.inclusive;
          x.unit = stock?.unit;
          x.tax = stock?.tax;
        });
      } catch (err) {
      }
    }),
  );
};
